import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  ArrowLeft,
  Save,
  Loader2,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Info,
  Star,
  Eye,
  EyeOff,
  Link2,
  Unlink,
  ShieldCheck,
} from 'lucide-react'
import PortalLogo from '../../components/integracoes/PortalLogo'
import { Panel, Button, buttonClass } from '../../design-system'

const inputClass =
  'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none'
const labelClass = 'block text-sm font-medium text-slate-700 mb-1'

interface PortalConfig {
  id: number
  portal: string
  nomeExibicao: string | null
  ativo: boolean
  autoPublicar: boolean
  autoRemoverVendido: boolean
  usarPrecoPromocional: boolean
  xmlFeedUrl: string | null
  xmlFeedAtivo: boolean
  ultimoTeste: string | null
  statusConexao: string
  ultimoErro: string | null
}

interface PlataformaConta {
  contaId: string | null
  contaNome: string | null
  contaEmail: string | null
  contaStatus: string | null
  planoNome: string | null
  planoTipo: string | null
  planoValor: number | null
  planoVencimento: string | null
  limiteAnunciosTotal: number | null
  limiteAnunciosDestaque: number | null
  limiteFotosPorAnuncio: number | null
  anunciosAtivos: number
  anunciosDestaque: number
  anunciosDisponiveis: number | null
  ultimaSincronizacao: string | null
}

interface TipoDestaque {
  id: number
  portal: string
  codigo: string
  nome: string
  descricao: string | null
  prioridade: number
  corBadge: string | null
  icone: string | null
  ativo: boolean
}

interface OAuthStatus {
  servidorConfigurado: boolean
  conectado: boolean
  statusConexao: string
  tokenExpiraEm: string | null
  ultimoTeste: string | null
  ultimoErro: string | null
  contaNome: string | null
  contaEmail: string | null
  contaId: string | null
}

const portalInfo: Record<string, { nome: string; campoApiKey: string; campoApiSecret: string; ajuda: string }> = {
  OLX: {
    nome: 'OLX',
    campoApiKey: 'Access Token ou Client ID',
    campoApiSecret: 'Client Secret (opcional se usar Access Token)',
    ajuda: 'Obtenha suas credenciais em https://developers.olx.com.br'
  },
  WEBMOTORS: {
    nome: 'WebMotors',
    campoApiKey: 'Token do Cockpit (Classificados Integrados)',
    campoApiSecret: '',
    ajuda: 'Gere o token no Cockpit Webmotors → aba "Classificados Integrados". As credenciais de integrador (Client ID/Secret) ficam no servidor.'
  },
  MERCADOLIVRE: {
    nome: 'Mercado Livre',
    campoApiKey: 'Access Token',
    campoApiSecret: 'Refresh Token (opcional)',
    ajuda: 'Obtenha suas credenciais em https://developers.mercadolivre.com.br'
  },
  ICARROS: {
    nome: 'iCarros',
    campoApiKey: 'Email',
    campoApiSecret: 'Senha',
    ajuda: 'Use suas credenciais de acesso ao painel iCarros'
  },
  MOBIAUTO: {
    nome: 'Mobiauto',
    campoApiKey: 'API Key',
    campoApiSecret: '',
    ajuda: 'Solicite sua API Key ao suporte Mobiauto'
  },
  USADOSBR: {
    nome: 'UsadosBR',
    campoApiKey: 'Login',
    campoApiSecret: 'Senha',
    ajuda: 'Use suas credenciais de acesso ao painel UsadosBR'
  },
  AUTOLINE: {
    nome: 'Autoline',
    campoApiKey: 'CNPJ',
    campoApiSecret: 'Senha',
    ajuda: 'Use seu CNPJ e senha de acesso ao painel Autoline'
  },
  NAPISTA: {
    nome: 'NaPista',
    campoApiKey: 'Email',
    campoApiSecret: 'Senha',
    ajuda: 'Use suas credenciais de acesso ao painel NaPista'
  },
}

export default function PlataformaConfig() {
  const { portal } = useParams<{ portal: string }>()
  const portalUpper = portal?.toUpperCase() || ''
  const navigate = useNavigate()
  const { showApiError, showSuccess, showInfo } = useNotification()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testando, setTestando] = useState(false)
  const [excluindo, setExcluindo] = useState(false)
  const [sincronizandoConta, setSincronizandoConta] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [showApiSecret, setShowApiSecret] = useState(false)

  const [config, setConfig] = useState<PortalConfig | null>(null)
  const [conta, setConta] = useState<PlataformaConta | null>(null)
  const [tiposDestaque, setTiposDestaque] = useState<TipoDestaque[]>([])
  const [isNew, setIsNew] = useState(false)

  // Portais que usam OAuth (não e-mail/senha): Mercado Livre e OLX
  const isMercadoLivre = portalUpper === 'MERCADOLIVRE'
  const isOlx = portalUpper === 'OLX'
  const isOAuth = isMercadoLivre || isOlx
  const oauthBasePath = isMercadoLivre ? 'mercadolivre' : 'olx'
  const oauthParam = isMercadoLivre ? 'ml' : 'olx'
  const oauthNome = isMercadoLivre ? 'Mercado Livre' : 'OLX'
  const [searchParams, setSearchParams] = useSearchParams()
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus | null>(null)
  const [conectandoOauth, setConectandoOauth] = useState(false)
  const [desconectandoOauth, setDesconectandoOauth] = useState(false)
  const [sincronizandoOlx, setSincronizandoOlx] = useState(false)

  // Webmotors usa modelo de integrador (client_id/secret no servidor) + token do Cockpit (não OAuth-redirect)
  const isWebmotors = portalUpper === 'WEBMOTORS'
  const [webmotorsStatus, setWebmotorsStatus] = useState<{
    servidorConfigurado: boolean; tokenCockpitDefinido: boolean; conectado: boolean
    statusConexao: string; ultimoErro: string | null
  } | null>(null)
  const [testandoWm, setTestandoWm] = useState(false)
  const [sincronizandoWm, setSincronizandoWm] = useState(false)

  const [form, setForm] = useState({
    nomeExibicao: '',
    apiKey: '',
    apiSecret: '',
    ativo: true,
    autoPublicar: false,
    autoRemoverVendido: true,
    usarPrecoPromocional: false,
    xmlFeedUrl: '',
    xmlFeedAtivo: false,
  })

  const info = portalInfo[portalUpper] || { nome: portalUpper, campoApiKey: 'API Key', campoApiSecret: 'API Secret', ajuda: '' }

  useEffect(() => {
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [portalUpper])

  // OAuth (Mercado Livre / OLX): carrega status e trata o retorno do callback (?ml|?olx=connected|error)
  useEffect(() => {
    if (!isOAuth) return
    const controller = new AbortController()

    const retorno = searchParams.get(oauthParam)
    if (retorno === 'connected') {
      showSuccess(`Conta do ${oauthNome} conectada com sucesso!`)
    } else if (retorno === 'error') {
      const motivo = searchParams.get('motivo') || 'desconhecido'
      showApiError({ message: `Falha ao conectar com o ${oauthNome} (${motivo}). Tente novamente.` })
    }
    if (retorno) {
      // Limpa os parâmetros da URL para não repetir a notificação
      searchParams.delete(oauthParam)
      searchParams.delete('motivo')
      setSearchParams(searchParams, { replace: true })
    }

    fetchOAuthStatus(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOAuth, oauthParam])

  const fetchOAuthStatus = async (signal?: AbortSignal) => {
    try {
      const response = await api.get<OAuthStatus>(`/integracoes/${oauthBasePath}/status`, { signal })
      setOauthStatus(response.data)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        // status indisponível não é crítico
      }
    }
  }

  const conectarOAuth = async () => {
    try {
      setConectandoOauth(true)
      const response = await api.get<{ authorizationUrl: string }>(`/integracoes/${oauthBasePath}/oauth/authorize`)
      // Redireciona o navegador para o portal autorizar
      window.location.href = response.data.authorizationUrl
    } catch (error) {
      showApiError(error)
      setConectandoOauth(false)
    }
  }

  const desconectarOAuth = async () => {
    if (!window.confirm(`Tem certeza que deseja desconectar a conta do ${oauthNome}?`)) return
    try {
      setDesconectandoOauth(true)
      await api.post(`/integracoes/${oauthBasePath}/disconnect`)
      showSuccess(`Conta do ${oauthNome} desconectada.`)
      await fetchOAuthStatus()
      fetchData()
    } catch (error) {
      showApiError(error)
    } finally {
      setDesconectandoOauth(false)
    }
  }

  const sincronizarStatusOlx = async () => {
    try {
      setSincronizandoOlx(true)
      const response = await api.post('/integracoes/olx/sincronizar-status')
      const d = response.data as { atualizados: number; reconciliados: number; totalPublicadosNaOlx: number; mensagem?: string }
      showSuccess(`Status OLX sincronizado: ${d.reconciliados} reconciliado(s), ${d.atualizados} com link atualizado.`)
      if (d.mensagem) {
        showInfo(d.mensagem)
      }
    } catch (error) {
      showApiError(error)
    } finally {
      setSincronizandoOlx(false)
    }
  }

  // Webmotors: status + testar + sincronizar estoque
  useEffect(() => {
    if (!isWebmotors) return
    const controller = new AbortController()
    fetchWebmotorsStatus(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWebmotors])

  const fetchWebmotorsStatus = async (signal?: AbortSignal) => {
    try {
      const response = await api.get('/integracoes/webmotors/status', { signal })
      setWebmotorsStatus(response.data)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        // status indisponível não é crítico
      }
    }
  }

  const testarWebmotors = async () => {
    try {
      setTestandoWm(true)
      const response = await api.post('/integracoes/webmotors/testar')
      if (response.data.sucesso) {
        showSuccess(response.data.message)
      } else {
        showApiError({ message: response.data.message })
      }
      await fetchWebmotorsStatus()
    } catch (error) {
      showApiError(error)
    } finally {
      setTestandoWm(false)
    }
  }

  const sincronizarEstoqueWebmotors = async () => {
    try {
      setSincronizandoWm(true)
      const response = await api.post('/integracoes/webmotors/sincronizar-estoque')
      const d = response.data as { totalNaWebmotors: number; vinculados: number; naoEncontrados: number }
      showSuccess(`Estoque Webmotors sincronizado: ${d.vinculados} vinculado(s) de ${d.totalNaWebmotors} anúncio(s).`)
      fetchData()
    } catch (error) {
      showApiError(error)
    } finally {
      setSincronizandoWm(false)
    }
  }

  const fetchData = async (signal?: AbortSignal) => {
    try {
      setLoading(true)

      // Buscar configuração do portal
      const configResponse = await api.get('/portais', { signal })
      const portalConfig = configResponse.data.find((p: PortalConfig) => p.portal === portalUpper)

      if (portalConfig) {
        setConfig(portalConfig)
        setForm({
          nomeExibicao: portalConfig.nomeExibicao || info.nome,
          apiKey: '', // Não retornamos a API key por segurança
          apiSecret: '',
          ativo: portalConfig.ativo,
          autoPublicar: portalConfig.autoPublicar,
          autoRemoverVendido: portalConfig.autoRemoverVendido,
          usarPrecoPromocional: portalConfig.usarPrecoPromocional,
          xmlFeedUrl: portalConfig.xmlFeedUrl || '',
          xmlFeedAtivo: portalConfig.xmlFeedAtivo,
        })
        setIsNew(false)

        // Buscar dados da conta
        try {
          const contaResponse = await api.get(`/integracoes/plataformas/${portalUpper}/conta`, { signal })
          setConta(contaResponse.data)
        } catch {
          // Conta pode não existir ainda
        }
      } else {
        setIsNew(true)
        setForm(prev => ({ ...prev, nomeExibicao: info.nome }))
      }

      // Buscar tipos de destaque
      try {
        const tiposResponse = await api.get(`/integracoes/plataformas/${portalUpper}/tipos-destaque`, { signal })
        setTiposDestaque(tiposResponse.data)
      } catch {
        // Tipos de destaque podem não estar implementados ainda
        setTiposDestaque([])
      }

    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        showApiError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)

      if (isNew) {
        await api.post('/portais', {
          portal: portalUpper,
          nomeExibicao: form.nomeExibicao,
          apiKey: form.apiKey,
          apiSecret: form.apiSecret || null,
          autoPublicar: form.autoPublicar,
          autoRemoverVendido: form.autoRemoverVendido,
        })
        showSuccess('Plataforma configurada com sucesso!')
      } else {
        await api.put(`/portais/${portalUpper}`, {
          nomeExibicao: form.nomeExibicao,
          ativo: form.ativo,
          apiKey: form.apiKey || null,
          apiSecret: form.apiSecret || null,
          autoPublicar: form.autoPublicar,
          autoRemoverVendido: form.autoRemoverVendido,
          usarPrecoPromocional: form.usarPrecoPromocional,
          xmlFeedUrl: form.xmlFeedUrl || null,
          xmlFeedAtivo: form.xmlFeedAtivo,
        })
        showSuccess('Configurações salvas com sucesso!')
      }

      setForm(prev => ({ ...prev, apiKey: '', apiSecret: '' }))
      fetchData()
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  const testarConexao = async () => {
    try {
      setTestando(true)
      const response = await api.post(`/portais/${portalUpper}/testar`)
      if (response.data.sucesso) {
        showSuccess(response.data.message || 'Conexão bem sucedida')
      } else {
        showApiError({ message: response.data.message || 'Falha na conexão' })
      }
      fetchData()
    } catch (error) {
      showApiError(error)
    } finally {
      setTestando(false)
    }
  }

  const sincronizarConta = async () => {
    try {
      setSincronizandoConta(true)
      const response = await api.post(`/integracoes/plataformas/${portalUpper}/sincronizar-conta`)
      if (response.data.sucesso) {
        showSuccess(response.data.message)
        setConta(response.data.conta)
      } else {
        showApiError({ message: response.data.message })
      }
    } catch (error) {
      showApiError(error)
    } finally {
      setSincronizandoConta(false)
    }
  }

  const excluirConfig = async () => {
    if (!window.confirm('Tem certeza que deseja remover esta configuração?')) return

    try {
      setExcluindo(true)
      await api.delete(`/portais/${portalUpper}`)
      showSuccess('Configuração removida com sucesso!')
      navigate('/integracoes/plataformas')
    } catch (error) {
      showApiError(error)
    } finally {
      setExcluindo(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONECTADO':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />
      case 'ERRO':
        return <XCircle className="w-5 h-5 text-rose-500" />
      default:
        return <Clock className="w-5 h-5 text-slate-400" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/integracoes/plataformas')} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <PortalLogo portal={portal || ''} size="lg" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">{info.nome}</h1>
              <div className="flex items-center gap-2">
                {config && getStatusIcon(config.statusConexao)}
                <span className="text-sm text-slate-500">
                  {config?.statusConexao === 'CONECTADO' ? 'Conectado' :
                   config?.statusConexao === 'ERRO' ? 'Erro de conexão' :
                   isNew ? 'Nova configuração' : 'Não testado'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {!isNew && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={testarConexao} disabled={testando}>
              {testando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Testar Conexão
            </Button>
            <Button variant="danger" onClick={excluirConfig} disabled={excluindo}>
              {excluindo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Excluir
            </Button>
          </div>
        )}
      </div>

      {/* Erro de conexão */}
      {config?.statusConexao === 'ERRO' && config.ultimoErro && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-rose-800">Erro de conexão</p>
            <p className="text-sm text-rose-600 mt-1">{config.ultimoErro}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <div className="lg:col-span-2 space-y-6">
          {/* Conexão OAuth (Mercado Livre / OLX) */}
          {isOAuth && (
            <Panel title={`Conexão com o ${oauthNome}`} icon={<Link2 className="h-4 w-4" />}>
              {oauthStatus && !oauthStatus.servidorConfigurado ? (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Integração não habilitada no servidor</p>
                    <p className="text-sm text-amber-700 mt-1">
                      {isOlx
                        ? 'A OLX exige credenciais de integrador (Client ID / Secret), solicitadas via suporteintegrador@olxbr.com. Após o administrador configurá-las, o botão "Conectar" ficará disponível.'
                        : 'O aplicativo do Mercado Livre ainda não foi configurado pelo administrador (App ID / Client Secret). Assim que for configurado, o botão "Conectar" ficará disponível.'}
                    </p>
                  </div>
                </div>
              ) : oauthStatus?.conectado ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 rounded-xl bg-emerald-50 ring-1 ring-emerald-100 p-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-emerald-800">Conta conectada</p>
                      {oauthStatus.contaNome && (
                        <p className="text-sm text-emerald-700">
                          {oauthStatus.contaNome}{oauthStatus.contaEmail ? ` · ${oauthStatus.contaEmail}` : ''}
                        </p>
                      )}
                      {oauthStatus.tokenExpiraEm && (
                        <p className="text-xs text-emerald-600/80 mt-0.5">
                          Token renova automaticamente · expira em {new Date(oauthStatus.tokenExpiraEm).toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isOlx && (
                      <Button variant="primary" onClick={sincronizarStatusOlx} disabled={sincronizandoOlx}>
                        {sincronizandoOlx ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Sincronizar status
                      </Button>
                    )}
                    <Button variant="secondary" onClick={conectarOAuth} disabled={conectandoOauth}>
                      {conectandoOauth ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Reconectar
                    </Button>
                    <Button variant="danger" onClick={desconectarOAuth} disabled={desconectandoOauth}>
                      {desconectandoOauth ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                      Desconectar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <ShieldCheck className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <p>
                      O {oauthNome} usa autorização segura (OAuth) — você entra com a sua conta
                      diretamente no site do {oauthNome}, sem precisar informar e-mail e senha aqui.
                    </p>
                  </div>
                  <Button variant="primary" onClick={conectarOAuth} disabled={conectandoOauth || (oauthStatus !== null && !oauthStatus.servidorConfigurado)}>
                    {conectandoOauth ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    Conectar com o {oauthNome}
                  </Button>
                  {oauthStatus?.ultimoErro && (
                    <p className="text-xs text-rose-500">{oauthStatus.ultimoErro}</p>
                  )}
                </div>
              )}
            </Panel>
          )}

          {/* Webmotors - Integração via integrador (token do Cockpit) */}
          {isWebmotors && (
            <Panel title="Integração Webmotors (Estoque de Canais)" icon={<Link2 className="h-4 w-4" />}>
              {webmotorsStatus && !webmotorsStatus.servidorConfigurado ? (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Integrador não configurado no servidor</p>
                    <p className="text-sm text-amber-700 mt-1">
                      As credenciais de integrador (Client ID / Client Secret) precisam ser cadastradas pelo
                      administrador no portal Sensedia da Webmotors. Após isso, cole o token do Cockpit abaixo e sincronize.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <ShieldCheck className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <p>
                      Cole o <strong>token do Cockpit</strong> (aba "Classificados Integrados") no campo abaixo em
                      "Configurações" e salve. Depois teste a conexão e sincronize o estoque para trazer status e
                      link dos anúncios por veículo.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="secondary" onClick={testarWebmotors} disabled={testandoWm || !webmotorsStatus?.tokenCockpitDefinido}>
                      {testandoWm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      Testar conexão
                    </Button>
                    <Button variant="primary" onClick={sincronizarEstoqueWebmotors} disabled={sincronizandoWm || !webmotorsStatus?.tokenCockpitDefinido}>
                      {sincronizandoWm ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Sincronizar estoque
                    </Button>
                  </div>
                  {webmotorsStatus && !webmotorsStatus.tokenCockpitDefinido && (
                    <p className="text-xs text-amber-600">Cole e salve o token do Cockpit em "Configurações" para habilitar.</p>
                  )}
                  {webmotorsStatus?.ultimoErro && (
                    <p className="text-xs text-rose-500">{webmotorsStatus.ultimoErro}</p>
                  )}
                </div>
              )}
            </Panel>
          )}

          <Panel title="Configurações">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome de exibição */}
              <div>
                <label className={labelClass}>
                  Nome de Exibição
                </label>
                <input
                  type="text"
                  value={form.nomeExibicao}
                  onChange={(e) => setForm({ ...form, nomeExibicao: e.target.value })}
                  className={inputClass}
                  placeholder={info.nome}
                />
              </div>

              {/* Credenciais (ocultas para portais OAuth: Mercado Livre e OLX) */}
              {!isOAuth && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    {info.campoApiKey} {isNew && <span className="text-rose-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={form.apiKey}
                      onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                      className={`${inputClass} pr-10`}
                      placeholder={isNew ? 'Obrigatório' : 'Deixe em branco para manter o atual'}
                      required={isNew}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {info.campoApiSecret && (
                  <div>
                    <label className={labelClass}>
                      {info.campoApiSecret}
                    </label>
                    <div className="relative">
                      <input
                        type={showApiSecret ? 'text' : 'password'}
                        value={form.apiSecret}
                        onChange={(e) => setForm({ ...form, apiSecret: e.target.value })}
                        className={`${inputClass} pr-10`}
                        placeholder="Deixe em branco para manter o atual"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiSecret(!showApiSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                )}

                {info.ajuda && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    {info.ajuda}
                  </p>
                )}
              </div>
              )}

              {/* Opções */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <h3 className="text-sm font-medium text-slate-900">Opções de Publicação</h3>

                {!isNew && (
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={form.ativo}
                      onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-slate-700">Plataforma ativa</span>
                  </label>
                )}

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={form.autoPublicar}
                    onChange={(e) => setForm({ ...form, autoPublicar: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Auto-publicar novos veículos</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={form.autoRemoverVendido}
                    onChange={(e) => setForm({ ...form, autoRemoverVendido: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Auto-remover quando vendido</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={form.usarPrecoPromocional}
                    onChange={(e) => setForm({ ...form, usarPrecoPromocional: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-slate-700">Usar preço promocional quando disponível</span>
                </label>
              </div>

              <div className="flex justify-end pt-4">
                <button type="submit" disabled={saving} className={buttonClass('primary')}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isNew ? 'Configurar Plataforma' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </Panel>

          {/* Tipos de Destaque */}
          {tiposDestaque.length > 0 && (
            <Panel title="Tipos de Destaque" icon={<Star className="h-4 w-4" />}>
              <div className="space-y-2">
                {tiposDestaque.map((tipo) => (
                  <div
                    key={tipo.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 ring-1 ring-slate-100"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: tipo.corBadge || '#6B7280' }}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{tipo.nome}</p>
                      <p className="text-xs text-slate-500">{tipo.descricao}</p>
                    </div>
                    <span className="text-xs text-slate-400 tabular-nums">
                      Prioridade: {tipo.prioridade}
                    </span>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </div>

        {/* Sidebar - Informações da Conta */}
        <div className="space-y-6">
          {!isNew && (
            <Panel
              title="Conta na Plataforma"
              action={
                <button
                  onClick={sincronizarConta}
                  disabled={sincronizandoConta}
                  className="text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  title="Sincronizar dados da conta"
                >
                  {sincronizandoConta ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-5 h-5" />
                  )}
                </button>
              }
            >
              {conta ? (
                <div className="space-y-4">
                  {conta.contaNome && (
                    <div>
                      <p className="text-xs text-slate-500">Nome</p>
                      <p className="font-medium text-slate-900">{conta.contaNome}</p>
                    </div>
                  )}

                  {conta.contaEmail && (
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm text-slate-600">{conta.contaEmail}</p>
                    </div>
                  )}

                  {conta.planoNome && (
                    <div>
                      <p className="text-xs text-slate-500">Plano</p>
                      <p className="font-medium text-slate-900">{conta.planoNome}</p>
                      {conta.planoVencimento && (
                        <p className="text-xs text-slate-500">
                          Vence em: {new Date(conta.planoVencimento).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                  )}

                  {conta.limiteAnunciosTotal && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Uso do Limite</p>
                      <div className="flex justify-between text-sm mb-1 tabular-nums">
                        <span>{conta.anunciosAtivos} ativos</span>
                        <span>{conta.limiteAnunciosTotal} máx</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            (conta.anunciosAtivos / conta.limiteAnunciosTotal) >= 0.9 ? 'bg-rose-500' :
                            (conta.anunciosAtivos / conta.limiteAnunciosTotal) >= 0.8 ? 'bg-amber-500' :
                            'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min((conta.anunciosAtivos / conta.limiteAnunciosTotal) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {conta.ultimaSincronizacao && (
                    <p className="text-xs text-slate-400">
                      Última sincronização: {new Date(conta.ultimaSincronizacao).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-2">Dados da conta não disponíveis</p>
                  <button
                    onClick={sincronizarConta}
                    disabled={sincronizandoConta}
                    className={buttonClass('secondary', 'sm')}
                  >
                    {sincronizandoConta ? 'Sincronizando...' : 'Buscar dados'}
                  </button>
                </div>
              )}
            </Panel>
          )}

          {/* Info */}
          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-sky-800">Dica</p>
                <p className="text-sm text-sky-700 mt-1">
                  Após salvar as credenciais, clique em "Testar Conexão" para verificar se a integração está funcionando corretamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
